import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWallet, FaInfoCircle, FaHistory } from 'react-icons/fa';
import { AffiliateApiService } from '../../service/affiliateApiService';
import { useLoader } from '../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../base/context/MessageProvider';
import './affiliate.css';

export default function AffiliateWithdraw() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();

  const fetchWithdraw = () => {
    loader.showLoading(true);
    AffiliateApiService.getWithdraw()
      .then((res) => {
        loader.showLoading(false);
        if (res.status?.code === 200 && res.data) {
          setData(res.data);
        } else {
          setError(res.error?.message || 'Failed to load');
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        if (err?.status === 403) {
          navigate('/', { replace: true });
          return;
        }
        setError(err?.message || 'Failed to load');
      });
  };

  useEffect(() => {
    fetchWithdraw();
  }, []);

  const minWithdrawal = Number(data?.minWithdrawal ?? 5000);

  const handleSubmit = (e) => {
    e.preventDefault();
    const num = Number(amount);
    if (!num || num < minWithdrawal) {
      message.showToast(`Minimum withdrawal is ₹${minWithdrawal.toLocaleString('en-IN')}`, ToastTypes.ERROR);
      return;
    }
    if (data && num > (data.walletBalance || 0)) {
      message.showToast('Insufficient IM Cash balance', ToastTypes.ERROR);
      return;
    }
    setSubmitting(true);
    AffiliateApiService.postWithdraw({ amount: num })
      .then((res) => {
        setSubmitting(false);
        if (res.status?.code === 200) {
          message.showToast('Withdrawal request submitted', ToastTypes.SUCCESS);
          setAmount('');
          fetchWithdraw();
        } else {
          message.showToast(res.error?.message || 'Request failed', ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        setSubmitting(false);
        message.showToast(err?.message || 'Request failed', ToastTypes.ERROR);
      });
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '–');
  const formatMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  if (error && !data) {
    return (
      <div className="affiliate-page">
        <h1 className="affiliate-welcome">Withdraw</h1>
        <p className="affiliate-error">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const balance = Number(data.walletBalance || 0);
  const withdrawals = data.withdrawals || [];

  return (
    <div className="affiliate-page">
      <h1 className="affiliate-welcome">Withdraw</h1>
      <p className="affiliate-subtitle">Withdraw your affiliate earnings to your bank account. Minimum ₹{minWithdrawal.toLocaleString('en-IN')} per request.</p>

      <div className="affiliate-cards" style={{ marginBottom: '1.75rem' }}>
        <div className="affiliate-card">
          <div className="affiliate-card-icon"><FaWallet size={20} /></div>
          <div className="affiliate-card-body">
            <div className="affiliate-card-label">Available balance</div>
            <div className="affiliate-card-value">{formatMoney(data.walletBalance)}</div>
          </div>
        </div>
        <div className="affiliate-card">
          <div className="affiliate-card-body">
            <div className="affiliate-card-label">Minimum withdrawal</div>
            <div className="affiliate-card-value">{formatMoney(minWithdrawal)}</div>
          </div>
        </div>
      </div>

      <div className="affiliate-referral-section">
        <h2 className="affiliate-section-title">Request withdrawal</h2>
        <p className="affiliate-section-desc">
          <FaInfoCircle style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
          Enter the amount you want to withdraw. Requests are processed as per our payout schedule. Ensure your bank details are updated in Profile.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="affiliate-form-group">
            <label>Amount (₹)</label>
            <input
              type="number"
              min={minWithdrawal}
              step="100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Min ₹${minWithdrawal.toLocaleString('en-IN')}`}
            />
          </div>
          <button type="submit" className="affiliate-btn affiliate-btn-primary" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
        </form>
      </div>

      <div className="affiliate-section">
        <h2 className="affiliate-section-title">
          <FaHistory size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
          Withdrawal history
        </h2>
        <p className="affiliate-section-desc">Your recent withdrawal requests and their status.</p>
        {withdrawals.length === 0 ? (
          <p className="affiliate-empty">No withdrawals yet.</p>
        ) : (
          <div className="affiliate-withdrawal-list">
            {withdrawals.map((w) => (
              <div key={w._id} className="affiliate-withdrawal-item">
                <span>{formatMoney(w.amount)} · {formatDate(w.requestedAt || w.createdAt)}</span>
                <span className={`status-${(w.status || '').toLowerCase()}`}>{w.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
